import { useState, useRef, useEffect, useMemo } from "react";
import JSZip from "jszip";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toCsv } from "@/lib/csv";
import { ToolContainer, DownloadButton, downloadBlob } from "@/components/ToolLayout";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ToolInfoSection } from "@/components/ToolInfoSection";
import { toast } from "sonner";
import { barcodeFileName, GS1_FORMATS, parseBarcodeList } from "@/lib/qrHelpers";
import { BARCODE_FORMATS, type BarcodeFormat, generateBarcodePng, generateBarcodeSvg, PRINT_DPI } from "@/lib/codeGeneration";

const MAX_LIST = 200;

const TOOL_INFO_CONTENT = {
  what: `Generates a real, scannable barcode — Code 128, EAN-13, EAN-8, UPC-A, Code 39, or ITF-14 — from a number or text value, ready to download and use on inventory labels, product packaging, or shipping materials.`,
  how: `Choose a barcode format, enter the value to encode, and optionally describe what the barcode is for — that description is printed above the barcode in the downloaded PNG (the SVG download leaves it out), so anyone looking at the file later (not just the barcode reader) knows what it's for. You can switch the digits under the bars on or off and pick a taller or shorter bar. For many labels at once, choose "A list": paste one value per line and download one ZIP with a file per value; lines that cannot be encoded are listed with the reason and left out.`,
  tip: `Each format has its own rules for valid input — EAN-13 needs 12 digits (the check digit is added for you), UPC-A needs 11, Code 128 accepts letters, numbers and symbols but no accents. If generation fails the tool tells you exactly what to change. The PNG downloads at 4× (300 dpi) by default; the 1× option is for screens, and the SVG scales to any print size.`,
};

export default function BarcodeGenerator() {
  const [value, setValue] = useState("590123412345");
  const [format, setFormat] = useState<BarcodeFormat>("EAN13");
  const [caption, setCaption] = useState("");
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [error, setError] = useState<string | null>(null);
  const [pngBytes, setPngBytes] = useState<Uint8Array | null>(null);
  const [scale, setScale] = useState("4"); // print-ready by default: 300 dpi PNG
  const [mode, setMode] = useState<"one" | "list">("one");
  const [showText, setShowText] = useState(true); // the human-readable digits under the bars
  const [barHeight, setBarHeight] = useState("80");
  const [listText, setListText] = useState("");
  const [zipFormat, setZipFormat] = useState<"png" | "svg">("png");
  const [building, setBuilding] = useState(false);
  const [basePx, setBasePx] = useState(0); // width of the 1x preview, to work out the printed size of the 4x export

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!value.trim()) { setError(null); setPngBytes(null); return; }
      try {
        const bytes = await generateBarcodePng(value, format, { caption, displayValue: showText, barHeight: Number(barHeight) });
        if (cancelled) return;
        setError(null);
        setPngBytes(bytes);
        const canvas = canvasRef.current;
        if (canvas) {
          // Same fix already established elsewhere in this codebase
          // (e.g. InvoiceGenerator's PDF download) for this exact
          // Uint8Array/BlobPart type mismatch -- wrapping in a fresh
          // Uint8Array satisfies BlobPart's expected shape.
          const blob = new Blob([new Uint8Array(bytes)], { type: "image/png" });
          const url = URL.createObjectURL(blob);
          const img = new Image();
          img.onload = () => {
            setBasePx(img.width);
            canvas.width = img.width;
            canvas.height = img.height;
            canvas.getContext("2d")?.drawImage(img, 0, 0);
            URL.revokeObjectURL(url);
          };
          img.src = url;
        }
      } catch (err) {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : "Couldn't generate this barcode");
        setPngBytes(null);
      }
    })();
    return () => { cancelled = true; };
  }, [value, format, caption, showText, barHeight]);

  const list = useMemo(() => parseBarcodeList(listText, format, zipFormat), [listText, format, zipFormat]);
  const listRows = list.rows.slice(0, MAX_LIST);
  const overCap = list.rows.length > MAX_LIST;

  const downloadZip = async () => {
    if (listRows.length === 0 || building) return;
    setBuilding(true);
    try {
      const zip = new JSZip();
      const opts = { displayValue: showText, barHeight: Number(barHeight) };
      for (let i = 0; i < listRows.length; i++) {
        const r = listRows[i];
        if (zipFormat === "svg") zip.file(r.file, generateBarcodeSvg(r.value, format, opts));
        else zip.file(r.file, await generateBarcodePng(r.value, format, { ...opts, scale: 4, dpi: PRINT_DPI }));
        if (i % 5 === 4) await new Promise((res) => setTimeout(res, 0)); // let the page breathe on long lists
      }
      zip.file("index.csv", toCsv(
        listRows.map((r) => ({ file: r.file, value: r.value })),
        [{ key: "file", header: "file" }, { key: "value", header: "value" }],
      ));
      const blob = await zip.generateAsync({ type: "blob" });
      downloadBlob(blob, `barcodes-${format.toLowerCase()}.zip`);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Couldn't build the ZIP");
    } finally {
      setBuilding(false);
    }
  };

  const download = async () => {
    if (!pngBytes) { toast.error("Fix the barcode value first"); return; }
    try {
      const bytes = scale === "1" ? pngBytes : await generateBarcodePng(value, format, { caption, displayValue: showText, barHeight: Number(barHeight), scale: Number(scale), ...(scale === "4" ? { dpi: PRINT_DPI } : {}) });
      downloadBlob(new Blob([new Uint8Array(bytes)], { type: "image/png" }), barcodeFileName(value, format, "png"));
    } catch (err) { toast.error(err instanceof Error ? err.message : "Couldn't generate this barcode"); }
  };

  const downloadSvg = () => {
    try {
      downloadBlob(new Blob([generateBarcodeSvg(value, format, { displayValue: showText, barHeight: Number(barHeight) })], { type: "image/svg+xml" }), barcodeFileName(value, format, "svg"));
    } catch (err) { toast.error(err instanceof Error ? err.message : "Couldn't generate this barcode"); }
  };

  return (
    <ToolContainer>
      <div className="max-w-md mx-auto space-y-4">
        <div>
          <Label htmlFor="bc-format">Format</Label>
          <Select value={format} onValueChange={(v) => setFormat(v as BarcodeFormat)}>
            <SelectTrigger id="bc-format"><SelectValue /></SelectTrigger>
            <SelectContent>
              {BARCODE_FORMATS.map((f) => (
                <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex gap-2" role="group" aria-label="One barcode or a list">
          <Button size="sm" variant={mode === "one" ? "default" : "outline"} onClick={() => setMode("one")} data-testid="bc-mode-one">One barcode</Button>
          <Button size="sm" variant={mode === "list" ? "default" : "outline"} onClick={() => setMode("list")} data-testid="bc-mode-list">A list (ZIP)</Button>
        </div>
        {mode === "one" && <div>
          <Label htmlFor="bc-value">Barcode Value</Label>
          <Input id="bc-value" type="text" inputMode={["EAN13", "EAN8", "UPC", "ITF14"].includes(format) ? "numeric" : "text"} autoComplete="off" spellCheck={false} value={value} onChange={(e) => setValue(e.target.value)} aria-invalid={!!error || undefined} data-testid="bc-value" />
          {!value.trim() && <p role="alert" className="text-xs text-destructive mt-1" data-testid="bc-empty">Enter a value to encode; Download stays off until you do.</p>}
          {GS1_FORMATS.includes(format) && (
            <p className="text-xs text-muted-foreground mt-1" data-testid="bc-gs1-note">To sell in shops or marketplaces, the number must come from GS1 (your company prefix). Numbers made here are fine for internal use, samples and testing.</p>
          )}
          {format === "CODE39" && value !== value.toUpperCase() && !error && (
            <p className="text-xs text-muted-foreground mt-1" data-testid="bc-uppercase-note">Code 39 has no lower-case letters, so this encodes {value.trim().toUpperCase()}.</p>
          )}
        </div>}
        {mode === "one" && <div>
          <Label htmlFor="bc-caption">What is this barcode for? (optional)</Label>
          <Input id="bc-caption" type="text" value={caption} onChange={(e) => setCaption(e.target.value)} placeholder="e.g. Blue T-Shirt, Size M" />
        </div>}
        {mode === "list" && (
          <div>
            <Label htmlFor="bc-list">Barcode values, one per line</Label>
            <Textarea id="bc-list" value={listText} onChange={(e) => setListText(e.target.value)} spellCheck={false} autoComplete="off"
              placeholder={"590123412345\n401234567890\n..."} className="min-h-[160px] font-mono" data-testid="bc-list" />
            {GS1_FORMATS.includes(format) && (
              <p className="text-xs text-muted-foreground mt-1" data-testid="bc-gs1-note">To sell in shops or marketplaces, the numbers must come from GS1 (your company prefix). Numbers made here are fine for internal use, samples and testing.</p>
            )}
            <p className="text-xs text-muted-foreground mt-1" data-testid="bc-list-summary" aria-live="polite">
              {listRows.length} barcode{listRows.length === 1 ? "" : "s"} ready{list.problems.length > 0 ? `, ${list.problems.length} line${list.problems.length === 1 ? "" : "s"} with a problem` : ""}{list.duplicates > 0 ? `, ${list.duplicates} repeated value${list.duplicates === 1 ? "" : "s"} made once` : ""}.
              {overCap ? ` Only the first ${MAX_LIST} are made.` : ""} The check digit is added where the format needs one.
            </p>
            {list.problems.length > 0 && (
              <ul role="alert" className="mt-2 space-y-1 text-sm text-destructive" data-testid="bc-list-problems">
                {list.problems.slice(0, 20).map((p) => <li key={p.line}>Line {p.line} ({p.input.length > 30 ? `${p.input.slice(0, 30)}…` : p.input}): {p.message}</li>)}
                {list.problems.length > 20 && <li>…and {list.problems.length - 20} more.</li>}
              </ul>
            )}
          </div>
        )}
        <div className="flex flex-wrap items-end gap-4">
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={showText} onChange={(e) => setShowText(e.target.checked)} data-testid="bc-show-text" />
            Show the number under the bars
          </label>
          <div className="w-40">
            <Label htmlFor="bc-height">Bar height</Label>
            <Select value={barHeight} onValueChange={setBarHeight}>
              <SelectTrigger id="bc-height"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="50">Short</SelectItem>
                <SelectItem value="80">Standard</SelectItem>
                <SelectItem value="120">Tall</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {mode === "one" && <div className="flex justify-center p-4 border rounded-lg bg-white min-h-[140px] items-center">
          {error && <p role="alert" data-testid="barcode-error" className="text-sm text-destructive text-center px-4">{error}</p>}
          {/* Always mounted (hidden while an error shows) so a valid value after an error redraws into a live canvas. */}
          <canvas ref={canvasRef} className={error || !pngBytes ? "hidden" : "max-w-full h-auto"} data-testid="barcode-canvas" />
        </div>}

        {mode === "list" && (
          <div className="flex items-end gap-2 flex-wrap">
            <div className="w-32">
              <Label htmlFor="bc-zip-format">Files in the ZIP</Label>
              <Select value={zipFormat} onValueChange={(v) => setZipFormat(v as "png" | "svg")}>
                <SelectTrigger id="bc-zip-format"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="png">PNG (300 dpi)</SelectItem>
                  <SelectItem value="svg">SVG (vector)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <DownloadButton onClick={downloadZip} label={building ? "Building ZIP…" : "Download ZIP"} disabled={listRows.length === 0 || building} />
          </div>
        )}
        {mode === "one" && <div className="flex items-end gap-2 flex-wrap">
          <div className="w-32">
            <Label htmlFor="bc-scale">PNG size</Label>
            <Select value={scale} onValueChange={setScale}>
              <SelectTrigger id="bc-scale"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="1">1× (small, screen)</SelectItem>
                <SelectItem value="2">2×</SelectItem>
                <SelectItem value="4">4× (print, 300 dpi)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {scale === "4" && basePx > 0 && (
            <p className="w-full text-xs text-muted-foreground order-last" data-testid="bc-physical">
              Prints {((basePx * 4) / PRINT_DPI).toFixed(2)} in ({((basePx * 4) / PRINT_DPI * 2.54).toFixed(1)} cm) wide at 100% ({basePx * 4} px at {PRINT_DPI} dpi).
            </p>
          )}
          <DownloadButton onClick={download} label="Download PNG" disabled={!pngBytes} />
          <DownloadButton onClick={downloadSvg} label="Download SVG" disabled={!pngBytes} />
        </div>}
      </div>
      <ToolInfoSection whatIsIt={TOOL_INFO_CONTENT.what} howToUse={TOOL_INFO_CONTENT.how} tip={TOOL_INFO_CONTENT.tip} />
    </ToolContainer>
  );
}
